import "./App.scss"
import "./helper/dataTableTheme"
import DefaultLayout from "./defaultLayout/DefaultLayout"
import { BrowserRouter, Route, Routes } from "react-router-dom"
import routes from "./router/routes"
import { UseContext } from "./usecontext/useContext"
import { ConfigProvider, theme } from "antd"
import { useTypedSelector } from "./hooks/useTypedSelector"
import { lazy, Suspense, useEffect } from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { TagsFiltersEnum } from "./modules/SearchTermsTrends/types/tag.type"
import { Themes } from "./redux/types/theme"
import SuspenseLoader from "./helper/loader/SuspenseLoader"

const Login = lazy(() => import("./pages/Login"))

dayjs.extend(utc)
dayjs.extend(timezone)

const App = () => {
  const { userTheme } = useTypedSelector((state) => state.theme)

  useEffect(() => {
    if (userTheme === Themes.DARK || userTheme === Themes.GEEK) {
      document.body.style.backgroundColor = "#000"
      document.body.classList.add("dark-theme")
      document.body.classList.remove("light-theme")
    } else {
      document.body.style.backgroundColor = "#0F0A1A"
      document.body.classList.add("light-theme")
      document.body.classList.remove("dark-theme")
    }
  }, [userTheme])

  useEffect(() => {
    const clearTagFilters = () => {
      localStorage.removeItem(TagsFiltersEnum.SEARCH_TERMS_TAGS_FILTERS)
      localStorage.removeItem(TagsFiltersEnum.CATEGORIES_TAGS_FILTERS)
    }

    clearTagFilters()
    window.addEventListener("beforeunload", clearTagFilters)

    return () => {
      window.removeEventListener("beforeunload", clearTagFilters)
    }
  }, [])

  const getThemeConfig = () => {
    switch (userTheme) {
      case Themes.DARK:
        return {
          algorithm: theme.darkAlgorithm,
        }
      case Themes.LIGTH:
        return {
          algorithm: theme.darkAlgorithm,
          token: {
            colorPrimary: "#7C3AED",
            colorSuccess: "#10B981",
            colorWarning: "#F59E0B",
            colorError: "#EF4444",
            colorInfo: "#3B82F6",
            colorTextBase: "#F8FAFC",
            colorBgBase: "#0F0A1A",
            colorPrimaryBg: "rgba(124, 58, 237, 0.12)",
            colorPrimaryBgHover: "rgba(124, 58, 237, 0.2)",
            colorPrimaryBorder: "#5B21B6",
            colorPrimaryBorderHover: "#7C3AED",
            colorPrimaryHover: "#A78BFA",
            colorPrimaryActive: "#6D28D9",
            colorPrimaryText: "#A78BFA",
            colorPrimaryTextHover: "#C4B5FD",
            colorPrimaryTextActive: "#8B5CF6",
            colorSuccessBg: "rgba(16, 185, 129, 0.12)",
            colorSuccessBgHover: "rgba(16, 185, 129, 0.2)",
            colorSuccessBorder: "#047857",
            colorSuccessBorderHover: "#10B981",
            colorSuccessHover: "#34D399",
            colorSuccessActive: "#059669",
            colorSuccessText: "#34D399",
            colorSuccessTextHover: "#6EE7B7",
            colorSuccessTextActive: "#10B981",
            colorWarningBg: "rgba(245, 158, 11, 0.12)",
            colorWarningBgHover: "rgba(245, 158, 11, 0.2)",
            colorWarningBorder: "#B45309",
            colorWarningBorderHover: "#F59E0B",
            colorWarningHover: "#FBBF24",
            colorWarningActive: "#D97706",
            colorWarningText: "#FBBF24",
            colorWarningTextHover: "#FCD34D",
            colorWarningTextActive: "#F59E0B",
            colorErrorBg: "rgba(239, 68, 68, 0.12)",
            colorErrorBgHover: "rgba(239, 68, 68, 0.2)",
            colorErrorBorder: "#B91C1C",
            colorErrorBorderHover: "#EF4444",
            colorErrorHover: "#F87171",
            colorErrorActive: "#DC2626",
            colorErrorText: "#F87171",
            colorErrorTextHover: "#FCA5A5",
            colorErrorTextActive: "#EF4444",
            colorInfoBg: "rgba(59, 130, 246, 0.12)",
            colorInfoBgHover: "rgba(59, 130, 246, 0.2)",
            colorInfoBorder: "#1D4ED8",
            colorInfoBorderHover: "#3B82F6",
            colorInfoHover: "#60A5FA",
            colorInfoActive: "#2563EB",
            colorInfoText: "#60A5FA",
            colorInfoTextHover: "#93C5FD",
            colorInfoTextActive: "#3B82F6",
            colorText: "rgba(248, 250, 252, 0.92)",
            colorTextSecondary: "rgba(248, 250, 252, 0.65)",
            colorTextTertiary: "rgba(248, 250, 252, 0.45)",
            colorTextQuaternary: "rgba(248, 250, 252, 0.25)",
            colorTextDisabled: "rgba(248, 250, 252, 0.25)",
            colorBgContainer: "#1A1128",
            colorBgElevated: "#241B35",
            colorBgLayout: "#0F0A1A",
            colorBgSpotlight: "#241B35",
            colorTextLightSolid: "#F8FAFC",
            colorBgMask: "rgba(15, 10, 26, 0.7)",
            colorBorder: "#2D2340",
            colorBorderSecondary: "#241B35",
            borderRadius: 8,
            borderRadiusXS: 4,
            borderRadiusSM: 6,
            borderRadiusLG: 12,
            padding: 12,
            paddingSM: 8,
            paddingLG: 16,
            margin: 12,
            marginSM: 8,
            marginLG: 16,
            fontFamily:
              "'Geist', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontSize: 14,
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.4), 0 1px 3px 0 rgba(124, 58, 237, 0.08)",
            boxShadowSecondary:
              "0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -2px rgba(124, 58, 237, 0.12)",
          },
        }
      case Themes.GEEK:
        return {
          algorithm: theme.darkAlgorithm,
          token: {
            colorPrimary: "#00ff9d",
            colorSuccess: "#39ff14",
            colorWarning: "#ffb800",
            colorError: "#ff0040",
            colorInfo: "#00e5ff",
            colorTextBase: "#c7f9cc",
            colorBgBase: "#0d1117",
            colorPrimaryBg: "#003b28",
            colorPrimaryBgHover: "#005a3c",
            colorPrimaryBorder: "#00cc7a",
            colorPrimaryBorderHover: "#00ff9d",
            colorPrimaryHover: "#00ff9d",
            colorPrimaryActive: "#00b36b",
            colorPrimaryText: "#00ff9d",
            colorPrimaryTextHover: "#33ffb0",
            colorPrimaryTextActive: "#00cc7a",
            colorSuccessBg: "#013d00",
            colorSuccessBgHover: "#026300",
            colorSuccessBorder: "#39ff14",
            colorSuccessBorderHover: "#5cff37",
            colorSuccessHover: "#5cff37",
            colorSuccessActive: "#2aff00",
            colorSuccessText: "#39ff14",
            colorSuccessTextHover: "#5cff37",
            colorSuccessTextActive: "#2aff00",
            colorWarningBg: "#3d2b00",
            colorWarningBgHover: "#5c4100",
            colorWarningBorder: "#ffb800",
            colorWarningBorderHover: "#ffc933",
            colorWarningHover: "#ffc933",
            colorWarningActive: "#e5a600",
            colorWarningText: "#ffb800",
            colorWarningTextHover: "#ffc933",
            colorWarningTextActive: "#e5a600",
            colorErrorBg: "#3d0010",
            colorErrorBgHover: "#5c0018",
            colorErrorBorder: "#ff0040",
            colorErrorBorderHover: "#ff3366",
            colorErrorHover: "#ff3366",
            colorErrorActive: "#cc0033",
            colorErrorText: "#ff0040",
            colorErrorTextHover: "#ff3366",
            colorErrorTextActive: "#cc0033",
            colorInfoBg: "#003540",
            colorInfoBgHover: "#004f5f",
            colorInfoBorder: "#00e5ff",
            colorInfoBorderHover: "#33ecff",
            colorInfoHover: "#33ecff",
            colorInfoActive: "#00b8cc",
            colorInfoText: "#00e5ff",
            colorInfoTextHover: "#33ecff",
            colorInfoTextActive: "#00b8cc",
            colorText: "rgba(199, 249, 204, 0.88)",
            colorTextSecondary: "rgba(199, 249, 204, 0.65)",
            colorTextTertiary: "rgba(199, 249, 204, 0.45)",
            colorTextQuaternary: "rgba(199, 249, 204, 0.25)",
            colorTextDisabled: "rgba(199, 249, 204, 0.25)",
            colorBgContainer: "#161b22",
            colorBgElevated: "#21262d",
            colorBgLayout: "#0d1117",
            colorBgSpotlight: "rgba(199, 249, 204, 0.85)",
            colorBgMask: "rgba(0, 0, 0, 0.8)",
            colorBorder: "#30363d",
            colorBorderSecondary: "#21262d",
            borderRadius: 2,
            borderRadiusXS: 1,
            borderRadiusSM: 1,
            borderRadiusLG: 4,
            padding: 12,
            paddingSM: 8,
            paddingLG: 16,
            margin: 12,
            marginSM: 8,
            marginLG: 16,
            boxShadow: "0 0 0 1px rgba(0, 255, 157, 0.2)",
            boxShadowSecondary: "0 0 0 1px rgba(0, 255, 157, 0.4)",
          },
        }
      default:
        return {
          algorithm: theme.defaultAlgorithm,
        }
    }
  }
  return (
    <ConfigProvider theme={getThemeConfig()}>
      <UseContext>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<SuspenseLoader />}>
            <Routes>
              <Route exact path="/login" element={<Login />}></Route>
              {routes.map((pages) => (
                <Route exact path="/" element={<DefaultLayout />} key={pages.path}>
                  <Route path={pages.path} exact element={pages.component} />
                </Route>
              ))}
            </Routes>
          </Suspense>
        </BrowserRouter>
      </UseContext>
    </ConfigProvider>
  )
}

export default App
